<template>
  <div>
    <a-modal
      id="group-approval-modal"
      v-model:visible="modalProps.visibility"
      :width="1000"
      title="Aprobación de un grupo de cobranzas"
      @cancel="closeModal"
    >
      <a-typography-title :level="3">Listado de cobranzas</a-typography-title>

      <a-form
        ref="formRefs"
        layout="vertical"
        :model="formState"
        :rules="rules"
      >
        <a-form-item
          v-if="hasPrecollections"
          ref="fileList"
          name="fileList"
          :style="{ textAlign: 'left' }"
        >
          <p>
            En el caso de que el cliente haya enviado una OC o HES debes cargar
            el/los documento(s).
          </p>
          <div style="max-width: 40%">
            <a-upload
              class="custom-upload"
              :file-list="formState.fileList"
              :remove="handleRemoveFromAll"
              :before-upload="(file) => beforeGlobalUpload(file)"
              :multiple="true"
            >
              <a-tooltip
                color="#05045D"
                title="Estos documentos se asociarán a cada Precobranza aprobada."
              >
                <a-button :style="{ width: '234px' }">
                  <upload-outlined></upload-outlined>
                  Cargar Documento Global
                </a-button>
              </a-tooltip>
            </a-upload>
          </div>
        </a-form-item>

        <p>Seleccione los tickets a aprobar:</p>
        <a-table
          :columns="columns"
          :dataSource="processedCollections"
          :loading="loadingCollections"
          rowKey="id"
        >
          <template #policies="{ record }">
            <div v-for="(policy, index) in record.policies" :key="index">
              {{ policy.numberPolicy }}
            </div>
          </template>

          <template #policiesType="{ record }">
            <div v-for="(policy, index) in record.policies" :key="index">
              {{ policy.policyConfiguration.collectionType }}
            </div>
          </template>

          <template #file="{ record }">
            <a-upload
              v-if="record.policies.some((policy) => policy.precollection)"
              :file-list="formState.rows[record.id]"
              :remove="(file) => handleRemoveSingle(file, record)"
              :before-upload="(file) => beforeSingleUpload(file, record)"
              :multiple="true"
              @change="(files) => handleRowUpload(files, record)"
            >
              <a-tooltip
                color="#05045D"
                title="Estos documentos se asociarán solo para esta Precobranza."
              >
                <a-button :style="{ width: '250px' }">
                  <upload-outlined></upload-outlined>
                  Cargar Documento Individual
                </a-button>
              </a-tooltip>
            </a-upload>
            <span v-else :style="{ color: 'gray' }">
              No es necesario subir archivo para cobranzas.
            </span>
          </template>
          <template #approve="{ record }">
            <a-checkbox
              v-model="record.approved"
              @change="toggleSelection(record)"
            ></a-checkbox>
            <a-typography-text class="ml-2">Aprobar</a-typography-text>
          </template>
        </a-table>

        <p>Mensaje adjunto:</p>
        <a-form-item name="message">
          <a-textarea
            :maxlength="600"
            placeholder="Resolución"
            v-model:value="formState.message"
            :autoSize="{ minRows: 5, maxRows: 9 }"
          />
        </a-form-item>
      </a-form>

      <a-typography-title class="text-gray-8 fw-regular mb-3" :level="5">
        Al aprobar estas cobranzas se le enviará la autorización de emisión de
        facturas a la compañía aseguradora además del mensaje y archivos
        adjuntos correspondientes.
      </a-typography-title>

      <a-row class="mt-1 mb-1">
        <a-col :span="2">
          <exclamation-circle-outlined
            :style="{ fontSize: '30px', color: '#FFBF01' }"
          />
        </a-col>
        <a-col :span="20" style="display: flex; align-items: center">
          <a-typography-text class="text-gray-8">
            No se está considerando ningún movimiento registrado antes o después
            de dicho periodo.
          </a-typography-text>
        </a-col>
      </a-row>

      <template #footer>
        <a-button :loading="modalProps.loading" @click="closeModal">
          Volver atrás
        </a-button>
        <a-button
          :loading="modalProps.loading"
          type="primary"
          @click="submitApprovalModal"
        >
          Sí, aprobar
        </a-button>
      </template>
    </a-modal>
    <a-modal
      v-model:visible="showSuccess"
      centered
      footer=""
      :maskClosable="false"
    >
      <a-result
        centered
        status="success"
        title="Cobranzas Aprobadas"
        sub-title="Las cobranzas han sido aprobadas con éxito."
      ></a-result>
    </a-modal>
  </div>
</template>

<script>
import {
  FETCH_COLLECTION_GROUP_COLLECTIONS,
  GET_COLLECTION_GROUP_COLLECTIONS,
  GET_LOGGED_USER,
  PATCH_APPROVE_COLLECTION_GROUP_COLLECTIONS
} from '@/store/types';
import notification from '@/utils/notifications';
import {
  ExclamationCircleOutlined,
  UploadOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { computed, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
  name: 'CollectionGroupApproveModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    idModal: {
      type: Number,
      default: 1
    },
    dataInfo: {
      type: Object,
      default: {}
    },
    collectionFormId: {
      type: [String, Number],
      default: 0
    }
  },
  components: {
    'exclamation-circle-outlined': ExclamationCircleOutlined,
    'upload-outlined': UploadOutlined
  },
  setup(props, { emit }) {
    // * Props
    const formRefs = ref();
    const insuranceData = computed(() => props.value);
    const showSuccess = ref(false);
    const store = useStore();
    const route = useRoute();
    const data = ref([]);
    const brokerId = store.getters[GET_LOGGED_USER].id;
    const loadingCollections = ref(false);
    const selectedCollections = ref([]);
    const hasPrecollections = ref(false);
    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id' },
      { title: 'Empresa', dataIndex: 'businessName', key: 'businessName' },
      {
        title: 'Póliza(s)',
        dataIndex: 'policies',
        key: 'policies',
        customRender: ({ record }) => {
          return record.policies && record.policies.length > 0
            ? record.policies.map((policy) => `${policy.number}`).join(', ')
            : '-';
        }
      },
      {
        title: 'Tipo de cobranza',
        dataIndex: 'policies',
        key: 'policiesType',
        customRender: ({ record }) => {
          if (record.policies && record.policies.length > 0) {
            const cobranzaTypes = record.policies
              .map((policy) =>
                policy.precollection ? 'Precobranza' : 'Cobranza'
              )
              .join(', ');
            return cobranzaTypes;
          } else {
            return '-';
          }
        }
      },
      {
        title: 'Archivo/s OC/HES para precobranza',
        dataIndex: 'file',
        key: 'file',
        slots: {
          customRender: 'file'
        }
      },
      {
        title: 'Seleccionar',
        dataIndex: 'approve',
        key: 'approve',
        slots: {
          customRender: 'approve'
        }
      }
    ];

    const emitResponse = () => {
      emit('update:modelValue', false);
    };

    const toggleSelection = (record) => {
      record.approved = !record.approved;
      const index = selectedCollections.value.findIndex(
        (item) => item.id === record.id
      );

      if (record.approved && index === -1) {
        selectedCollections.value.push({
          ...record,
          fileList: formState.rows[record.id] || []
        });
      } else if (!record.approved && index !== -1) {
        selectedCollections.value.splice(index, 1);
      }
    };

    const formState = reactive({
      fileList: [],
      message: '',
      rows: {}
    });
    const rules = {
      message: [
        {
          required: true,
          message: 'Ingrese mensaje',
          trigger: 'change',
          type: 'string'
        }
      ]
    };

    const processedCollections = computed(() => {
      const collections = store.getters[GET_COLLECTION_GROUP_COLLECTIONS].data;
      loadingCollections.value =
        store.getters[GET_COLLECTION_GROUP_COLLECTIONS].loading;

      return collections
        .map((obj) => ({
          id: obj.id,
          key: obj.id,
          collectionStatus: obj.status,
          invoiceStatus: obj.invoiceStatus,
          payrollStatus: obj.squaringStatus,
          subholding: obj.subHolding,
          businessName: obj.companies,
          policy: obj.policies,
          policies: obj.policiesWithConfigs?.map((policy) => ({
            number: policy.numberPolicy,
            type: policy.policyConfiguration.collectionType,
            precollection:
              policy.policyConfiguration.collectionType === 'precollection'
          })),
          detail: obj.id,
          approved: false
        }))
        .filter((obj) => obj.collectionStatus === 'HR_VALIDATION')
        .sort((a, b) => a.id - b.id);
    });

    watch(processedCollections, (newVal) => {
      hasPrecollections.value = newVal.some((collection) =>
        collection.policies.some((policy) => policy.precollection)
      );
    });

    const handleRowUpload = (files, row) => {
      const currentFiles = formState.rows[row.id] || [];

      const fileMap = new Map();

      currentFiles.forEach((file) => {
        const key = `${file.name}-${file.size}`;
        fileMap.set(key, file);
      });

      files.fileList.forEach((file) => {
        const key = `${file.name}-${file.size}`;
        if (!fileMap.has(key)) {
          fileMap.set(key, file);
        }
      });

      formState.rows[row.id] = Array.from(fileMap.values());
    };

    const handleRemoveFromAll = (file) => {
      if (file.id) {
        formState.removeFiles = [...formState.removeFiles, file.id];
      }
      const index = formState.fileList.indexOf(file);
      const newFileList = formState.fileList.slice();
      newFileList.splice(index, 1);
      formState.fileList = newFileList;
      this.$forceUpdate();
    };

    const handleRemoveSingle = (file, row) => {
      if (file.id) {
        row.removeFiles = [...row.removeFiles, file.id];
      }
      const index = formState.rows[row.id].indexOf(file);
      const newFileList = formState.rows[row.id].slice();
      newFileList.splice(index, 1);
      formState.rows[row.id] = newFileList;
      this.$forceUpdate();
    };

    const beforeGlobalUpload = (file) => {
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 5) {
        message.error('El archivo excede el peso máximo 5MB');
      } else if (formState.fileList.length === 5) {
        message.error('Máximo de archivos a subir es 5');
      } else {
        formState.fileList = [...formState.fileList, file];
      }
      return false;
    };

    const beforeSingleUpload = (file, row) => {
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 5) {
        message.error('El archivo excede el peso máximo 5MB');
      } else if (formState.rows[row.id]?.length === 5) {
        message.error('Máximo de archivos a subir es 5');
      } else {
        formState.rows[row.id] = [...(formState.rows[row.id] || []), file];
      }
      return false;
    };

    // * Modal
    const modalProps = reactive({
      visibility: false,
      title: 'Resumen aprobación de cobranzas',
      loading: false
    });

    const closeModal = () => {
      modalProps.visibility = false;
      formRefs.value.resetFields();
      emitResponse();
    };

    const submitApprovalModal = () => {
      if (selectedCollections.value.length === 0) {
        notification(
          'warning',
          'Advertencia',
          'Debes seleccionar al menos una cobranza o precobranza'
        );
        return;
      }

      formRefs.value.validate().then((resp) => {
        const { fileList, message } = resp;

        const formData = new FormData();
        formData.append('brokerId', brokerId);
        formData.append('message', message);

        if (fileList && fileList.length > 0) {
          fileList.forEach((file) => {
            formData.append('shared_files', file);
          });
        }

        if (selectedCollections.value.length > 0) {
          const selectedCollectionsArray = selectedCollections.value.map(
            (collection) => {
              return { id: collection.id };
            }
          );
          formData.append(
            'selectedCollections',
            JSON.stringify(selectedCollectionsArray)
          );

          selectedCollections.value.forEach((collection) => {
            formState.rows[collection.id]?.forEach((file) => {
              const newFile = new File(
                [file],
                `${collection.id}|${file.name}`,
                { type: file.type }
              );
              formData.append('specific_files', newFile);
            });
          });
        }
        approveCollections({
          collectionGroupId: props.collectionFormId,
          payload: formData
        });
      });
    };

    const approveCollections = async (data) => {
      try {
        modalProps.loading = true;
        const resp = await store.dispatch(
          PATCH_APPROVE_COLLECTION_GROUP_COLLECTIONS,
          data
        );
        if (resp.status == 200) {
          await store.dispatch(
            FETCH_COLLECTION_GROUP_COLLECTIONS,
            route.params.id
          );
          closeModal();
          notification(
            'success',
            'Correcto',
            'Listado de cobranzas aprobado con éxito'
          );
        }
      } catch (error) {
        console.error('Error aprobando las cobranzas:', error);
        notification('error', 'Error', error.response.data.message);
      } finally {
        modalProps.loading = false;
        selectedCollections.value = [];
        formState.fileList = [];
      }
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        modalProps.visibility = newVal;
      }
    );

    watch(
      () => store.getters[GET_COLLECTION_GROUP_COLLECTIONS],
      (newVal) => {
        if (newVal.length > 0) {
          loadingCollections.value = false;
        }
      },
      { immediate: true }
    );

    return {
      closeModal,
      modalProps,
      insuranceData,
      showSuccess,
      brokerId,
      hasPrecollections,
      loadingCollections,
      processedCollections,
      emitResponse,
      columns,
      data,
      approveCollections,
      formState,
      formRefs,
      rules,
      toggleSelection,
      beforeGlobalUpload,
      beforeSingleUpload,
      handleRemoveFromAll,
      handleRemoveSingle,
      handleRowUpload,
      selectedCollections,
      submitApprovalModal
    };
  }
};
</script>
